import React, { useState, useEffect } from "react";
import { useAuth } from "../../App";
import RSSelect from '../common/RSSelect';
import "./activities.css";

export default function Activities() {
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [classInfo, setClassInfo] = useState(null);
  const [weeks, setWeeks] = useState([]); // array of 35 week objects
  const [grades, setGrades] = useState([]);
  const [selectedGradeId, setSelectedGradeId] = useState(null);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load classes (to find teacher's class) and grades (for Mods)
  useEffect(() => {
    let mounted = true;
    async function loadMeta() {
      setLoading(true);
      setError(null);
      try {
        if (!apiUrl) {
          setError('API not configured. Please set REACT_APP_API_URL');
          return;
        }
        const token = localStorage.getItem('token');
        if (!token || !user || !user.role) {
          setError('Bạn cần đăng nhập để xem activities');
          return;
        }
        const role = user.role.toLowerCase();
        if (role !== 'teacher' && role !== 'mod') {
          setError('Trang Activities chỉ dành cho giáo viên');
          return;
        }

        // fetch classes and pick the teacher's class
        const cres = await fetch(`${apiUrl}/api/classes`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cres.ok) throw new Error('Failed to load classes');
        const cdata = await cres.json();
        const list = cdata.classes || [];
        const myClass = list.find(c => c.teacher_username === user.username) || list[0] || null;
        if (!mounted) return;
        setClassInfo(myClass);

        if (role === 'teacher') {
          setSelectedGradeId(myClass ? myClass.grade_id : null);
        }

        if (role === 'mod') {
          // load grades for mod to pick from
          const gres = await fetch(`${apiUrl}/api/grades`, { headers: { Authorization: `Bearer ${token}` } });
          if (gres.ok) {
            const gd = await gres.json();
            const gList = gd.grades || [];
            if (mounted) setGrades(gList);
            if (gList && gList.length) {
              // default to first grade if not already selected
              const firstId = gList[0].id;
              if (mounted) setSelectedGradeId(prev => prev || firstId);
            }
          }
        }
      } catch (e) {
        console.error(e);
        if (mounted) setError('Không thể tải dữ liệu');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadMeta();
    return () => { mounted = false; };
  }, [apiUrl, user]);

  // Load lessons/activities once we have a selectedGradeId
  useEffect(() => {
    let mounted = true;
    async function loadActivities() {
      if (!apiUrl) return;
      const token = localStorage.getItem('token');
      if (!token || !user || !user.role) return;
      if (!selectedGradeId) {
        setWeeks([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const lres = await fetch(`${apiUrl}/api/lessons`, { headers: { Authorization: `Bearer ${token}` } });
        if (!lres.ok) throw new Error('Failed to load lessons');
        const ld = await lres.json();
        const lessons = ld.lessons || [];

        const matched = lessons.filter(l => Number(l.grade_id) === Number(selectedGradeId));
        const sourceLessons = matched; // do NOT fall back to all lessons; only show matched grade activities

        if (!sourceLessons.length) {
          if (mounted) {
            setWeeks([]);
            setSelectedWeekIndex(0);
          }
          return;
        }

        const aggregated = [];
        for (const ls of sourceLessons) {
          const acts = ls.activities || [];
          if (!Array.isArray(acts)) continue;
          for (const a of acts) {
            const items = Array.isArray(a.items) ? a.items : (a.items && typeof a.items === 'string' ? a.items.split(',').map(s => s.trim()).filter(Boolean) : []);
            aggregated.push({ week: a.week, topic: a.topic || null, items });
          }
        }

        if (mounted) {
          setWeeks(aggregated);
          setSelectedWeekIndex(0);
        }
      } catch (e) {
        console.error(e);
        if (mounted) setError('Không thể tải activities');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadActivities();
    return () => { mounted = false; };
  }, [apiUrl, user, selectedGradeId]);

  const selectedWeek = weeks && weeks.length > 0 ? weeks[selectedWeekIndex] : null;

  return (
    <div className="activities-page">
      <aside className="activity-dates">
        <h4>Weeks</h4>
        {loading && <div className="muted">Loading...</div>}
        {!loading && weeks.length === 0 && <div className="muted">No activities</div>}
        {weeks.map((w, i) => (
          <button key={i} className={selectedWeekIndex === i ? 'date active' : 'date'} onClick={() => setSelectedWeekIndex(i)}>Tuần {w.week || (i + 1)}</button>
        ))}
      </aside>

      <main className="activity-main">
        <h2>
          Activities & Kits cho Lớp {classInfo ? (classInfo.name || `Grade ${classInfo && classInfo.grade_id}`) : '—'}
        </h2>
        {user && user.role && user.role.toLowerCase() === 'mod' && (
          <div className="mod-grade-select">
              <label className="label-inline">Chọn khối:</label>
              <RSSelect
                options={[{ value: '', label: '-- Chọn --' }].concat((grades || []).map(g => ({ value: g.id, label: g.name })))}
                value={selectedGradeId || ''}
                onChange={v => setSelectedGradeId(v ? Number(v) : null)}
                isClearable={true}
                placeholder="-- Chọn --"
              />
            </div>
        )}
        {error && <div className="error">{error}</div>}
        {!selectedWeek ? <p>Chọn một tuần để xem chi tiết</p> : (
          <div className="activity-list">
            <div className="activity-card activity-topic">
              <h4>Topic</h4>
              <div className="topic-content">
                  {selectedWeek.topic ? <div className="pre-wrap">{selectedWeek.topic}</div> : <div className="muted">Không có topic</div>}
                </div>
            </div>

            <div className="activity-card activity-items">
              <h4>Items</h4>
              {(selectedWeek.items && selectedWeek.items.length) ? (
                <ul>
                  {selectedWeek.items.map((it, idx) => <li key={idx}>{it}</li>)}
                </ul>
              ) : (
                <div className="muted">Không có items</div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
